import {
  DEFAULT_SUPPORT_URL,
  authClientSettingSchema,
  authProvidersOf,
  defaultAuthProvider,
  type AuthClientSetting,
  type AuthProvider,
} from "@shared/types/authClientConfig";
import { currentCorrelationId } from "../correlation";
import { idleTimeoutMinutesFrom } from "../env";
import { logger } from "../logger";

/**
 * The browser's sign-in configuration, read at runtime (Session 9, contract §2.5).
 *
 * One web image serves any identity provider: the server says which one through the public
 * `GET /api/auth/config`, and MSAL is built from the answer. There are no build-time sign-in
 * values (the one-release fallback was removed with the main flip, 2026-09-29), so anything
 * that is not a valid 200 is a failure the user can retry: a network error, a 5xx, a body that
 * fails the schema, and 404 `AUTH_CLIENT_NOT_CONFIGURED` too, which means "this server offers no
 * browser sign-in" (reason `not-configured`).
 *
 * Workforce SSO (2026-09-28): the answer may list several ways to sign in (`providers`), each
 * resolved here into everything its own MSAL client needs. The single form is one provider.
 */

/** Where a configuration came from: always the server, at runtime. */
export type AuthConfigSource = "runtime";

/** Everything MSAL needs for one way to sign in, with the defaults already applied. */
export interface ResolvedAuthConfig {
  source: AuthConfigSource;
  /** Stable key, persisted as the browser's choice (`providerChoice.ts`). `default` in the single form. */
  id: string;
  /** Button text. */
  label: string;
  /** The identity-provider family (`entra-external`, `entra-workforce`, `custom`). */
  idp: string;
  /** Sent as `domain_hint` (a Google IdP federated inside External ID). */
  domainHint?: string;
  /** The provider the email form starts and an unauthenticated visit starts on. */
  isDefault: boolean;
  authority: string;
  clientId: string;
  /** When set, only accounts from this directory may become active (see `msalTokenProvider`). */
  tenantId?: string;
  scopes: string[];
  /** Lower-case host names. An account's `environment` must be one of them. */
  knownAuthorities: string[];
  redirectUri: string;
  postLogoutRedirectUri: string;
}

/** Every way to sign in this deployment offers, and where "Contact Support" points. */
export interface ResolvedAuthSetup {
  /** At least one; exactly one has `isDefault`. */
  providers: ResolvedAuthConfig[];
  /** A `mailto:` or `https:` URL; `https://skypoint.ai/customer-support/` when the server names none. */
  supportUrl: string;
  /** SEC-14 automatic logoff, from the server's `IDLE_TIMEOUT_MINUTES` (round 9, fix F). */
  idleTimeoutMinutes: number;
}

/** Why the configuration could not be loaded. An identifier, never a value from the payload. */
export type AuthConfigFailure = "network" | "status" | "invalid" | "not-configured";

/** The sign-in configuration could not be loaded; the caller shows "unavailable" with Retry. */
export class AuthConfigUnavailableError extends Error {
  constructor(readonly reason: AuthConfigFailure) {
    super(
      "skyData could not load its sign-in settings. Check your connection, then retry. If it keeps happening, contact your administrator with the reference id.",
    );
    this.name = "AuthConfigUnavailableError";
  }
}

const CONFIG_PATH = "/api/auth/config";
/**
 * How long the browser waits for the config before showing Retry. Without a limit a server that
 * accepts the connection and never answers leaves the app on "Starting skyData…" for ever.
 */
const CONFIG_TIMEOUT_MS = 10_000;
const NOT_CONFIGURED = "AUTH_CLIENT_NOT_CONFIGURED";

function origin(): string {
  return typeof window === "undefined" ? "" : window.location.origin;
}

function fromProvider(config: AuthProvider, isDefault: boolean): ResolvedAuthConfig {
  return {
    source: "runtime",
    id: config.id,
    label: config.label,
    idp: config.idp,
    ...(config.domainHint ? { domainHint: config.domainHint } : {}),
    isDefault,
    authority: config.authority,
    clientId: config.clientId,
    ...(config.tenantId ? { tenantId: config.tenantId } : {}),
    scopes: config.scopes,
    knownAuthorities: config.knownAuthorities,
    redirectUri: config.redirectUri ?? `${origin()}/callback`,
    postLogoutRedirectUri: config.postLogoutRedirectUri ?? `${origin()}/sign-in`,
  };
}

async function readBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function errorCode(body: unknown): string | undefined {
  if (!body || typeof body !== "object") return undefined;
  const error = (body as { error?: unknown }).error;
  if (!error || typeof error !== "object") return undefined;
  const code = (error as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
}

function fail(reason: AuthConfigFailure, status?: number): never {
  logger.error("sign-in configuration could not be loaded", undefined, {
    "skydata.reason": reason,
    ...(status === undefined ? {} : { "skydata.status": status }),
  });
  throw new AuthConfigUnavailableError(reason);
}

/** The default provider of a resolved setup. */
export function defaultProviderOf(setup: ResolvedAuthSetup): ResolvedAuthConfig {
  return setup.providers.find((provider) => provider.isDefault) ?? setup.providers[0]!;
}

/**
 * The default provider's configuration: what MSAL started on before there was a choice.
 * `loadAuthSetup` is the whole answer.
 */
export async function loadAuthConfig(): Promise<ResolvedAuthConfig> {
  return defaultProviderOf(await loadAuthSetup());
}

/**
 * Fetch and validate the sign-in configuration: every provider, resolved.
 *
 * Plain `fetch`, never `apiRequest`: that attaches a token, and a token is what this is needed
 * to get. `no-store` because a cached answer would keep a browser on the old identity provider
 * after the flip, or after a rollback.
 */
export async function loadAuthSetup(): Promise<ResolvedAuthSetup> {
  let response: Response;
  try {
    response = await fetch(CONFIG_PATH, {
      method: "GET",
      cache: "no-store",
      credentials: "same-origin",
      // A timeout aborts the fetch, which lands in the catch below as a network failure.
      signal: AbortSignal.timeout(CONFIG_TIMEOUT_MS),
      headers: { accept: "application/json", "x-correlation-id": currentCorrelationId() },
    });
  } catch {
    return fail("network");
  }

  const body = await readBody(response);

  // No fallback: this server offers no browser sign-in (a local DEV_AUTH stack).
  if (response.status === 404 && errorCode(body) === NOT_CONFIGURED) {
    return fail("not-configured", response.status);
  }

  if (!response.ok) return fail("status", response.status);

  const data =
    body && typeof body === "object" && (body as { isSuccess?: unknown }).isSuccess === true
      ? (body as { data?: unknown }).data
      : undefined;
  const parsed = authClientSettingSchema.safeParse(data);
  if (!parsed.success) return fail("invalid", response.status);

  const listed = authProvidersOf(parsed.data);
  const fallback = defaultAuthProvider(listed);
  const providers = listed.map((provider) => fromProvider(provider, provider === fallback));
  logger.info("sign-in configuration loaded", {
    "skydata.source": "runtime",
    "skydata.idp": fallback.idp,
    "skydata.providers": providers.length,
  });
  return {
    providers,
    supportUrl: parsed.data.supportUrl ?? DEFAULT_SUPPORT_URL,
    // Beside the `AUTH_CLIENT` fields, so read from the raw answer: the schema strips it.
    idleTimeoutMinutes: idleTimeoutMinutesFrom(
      (data as { idleTimeoutMinutes?: unknown }).idleTimeoutMinutes,
    ),
  };
}

/**
 * #1639 C0: the same answer as the deployment's sign-in settings, whole (every provider with its
 * `pinEmailDomains`), for the grant dialog's "Sign in with" choice. Quiet: null on any failure,
 * a local `DEV_AUTH` stack's 404 included, and nothing is logged, because the dialog then simply
 * offers no choice and the server pins as before. Plain `fetch`, as `loadAuthSetup`: the route is
 * public and needs no token.
 */
export async function loadAuthClientSetting(
  signal?: AbortSignal,
): Promise<AuthClientSetting | null> {
  try {
    const response = await fetch(CONFIG_PATH, {
      method: "GET",
      cache: "no-store",
      credentials: "same-origin",
      signal: signal ?? AbortSignal.timeout(CONFIG_TIMEOUT_MS),
      headers: { accept: "application/json", "x-correlation-id": currentCorrelationId() },
    });
    if (!response.ok) return null;
    const body = await readBody(response);
    const data =
      body && typeof body === "object" && (body as { isSuccess?: unknown }).isSuccess === true
        ? (body as { data?: unknown }).data
        : undefined;
    const parsed = authClientSettingSchema.safeParse(data);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
